import { useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { dubaiFactories } from "@/mocks/dubaiFactories";
import { saudiFactories } from "@/mocks/saudiFactories";
import { qatarFactories } from "@/mocks/qatarFactories";
import { Factory } from "@/components/feature/FactoryCard";

const allFactories: Factory[] = [
  ...(dubaiFactories as unknown as Factory[]),
  ...(saudiFactories as unknown as Factory[]),
  ...(qatarFactories as unknown as Factory[]),
];

const FactoryProductDetailPage = () => {
  const { factoryId, productId } = useParams<{ factoryId: string; productId: string }>();
  const navigate = useNavigate();

  const factory = allFactories.find((f) => f.id === factoryId);
  const product = factory?.products.find((p) => p.id === productId);

  const [orderType, setOrderType] = useState<"sample" | "bulk" | null>(null);
  const [qty, setQty] = useState(1);
  const [submitted, setSubmitted] = useState(false);
  const [activeImage, setActiveImage] = useState(0);

  if (!factory || !product) {
    return (
      <div className="min-h-screen">
        <Navbar />
        <div className="flex items-center justify-center min-h-[60vh] flex-col gap-4">
          <i className="ri-error-warning-line text-5xl text-amber-300"></i>
          <p className="text-gray-500 text-lg">Product not found.</p>
          <button onClick={() => navigate(-1)} className="bg-amber-500 text-white px-6 py-2.5 rounded-xl font-semibold cursor-pointer whitespace-nowrap">Go Back</button>
        </div>
        <Footer />
      </div>
    );
  }

  const minQty = orderType === "bulk" ? product.moq : 1;
  const unitPrice = orderType === "bulk" ? product.bulkPrice : product.samplePrice;
  const total = (unitPrice * qty).toFixed(2);

  const images = [product.image, factory.coverImage, factory.logo];

  const relatedProducts = factory.products.filter((p) => p.id !== product.id);

  const countryRoute = factory.id.startsWith("df") ? "/dubai" : factory.id.startsWith("sf") ? "/saudi-arabia" : "/qatar";
  const countryLabel = factory.id.startsWith("df") ? "Dubai" : factory.id.startsWith("sf") ? "Saudi Arabia" : "Qatar";

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <div className="pt-24 pb-20 px-4 md:px-6">
        <div className="max-w-6xl mx-auto">

          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-xs text-gray-400 mb-8 flex-wrap">
            <Link to="/" className="hover:text-amber-600 cursor-pointer">Home</Link>
            <i className="ri-arrow-right-s-line"></i>
            <Link to={countryRoute} className="hover:text-amber-600 cursor-pointer">{countryLabel} Factories</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-gray-500 truncate max-w-[160px]">{factory.name}</span>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-amber-600 font-medium truncate max-w-[160px]">{product.name}</span>
          </nav>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 mb-16">
            {/* Left: Images */}
            <div>
              <div className="w-full h-80 md:h-96 rounded-2xl overflow-hidden border border-amber-100 mb-3">
                <img src={images[activeImage]} alt={product.name} className="w-full h-full object-cover object-top" />
              </div>
              <div className="flex gap-2">
                {images.map((img, i) => (
                  <button
                    key={i}
                    onClick={() => setActiveImage(i)}
                    className={`w-16 h-16 rounded-xl overflow-hidden border-2 transition-all cursor-pointer flex-shrink-0 ${activeImage === i ? "border-amber-500" : "border-gray-100 hover:border-amber-200"}`}
                  >
                    <img src={img} alt="" className="w-full h-full object-cover object-top" />
                  </button>
                ))}
              </div>
            </div>

            {/* Right: Info + Order */}
            <div className="flex flex-col gap-5">
              {/* Badges */}
              <div className="flex flex-wrap gap-2">
                <span className="bg-amber-100 text-amber-700 text-xs font-bold px-3 py-1 rounded-full">{product.category}</span>
                {factory.verified && (
                  <span className="bg-amber-500 text-white text-xs font-bold px-3 py-1 rounded-full flex items-center gap-1">
                    <i className="ri-shield-check-fill"></i> Verified Factory
                  </span>
                )}
                {factory.dropshipping && (
                  <span className="bg-emerald-500 text-white text-xs font-bold px-3 py-1 rounded-full flex items-center gap-1">
                    <i className="ri-truck-fill"></i> Dropshipping Available
                  </span>
                )}
              </div>

              {/* Title */}
              <h1 className="text-2xl md:text-3xl font-black text-gray-900 leading-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
                {product.name}
              </h1>

              {/* Factory info */}
              <Link to={countryRoute} className="flex items-center gap-3 bg-amber-50 border border-amber-100 rounded-xl px-4 py-3 hover:border-amber-300 transition-colors cursor-pointer">
                <img src={factory.logo} alt={factory.name} className="w-10 h-10 rounded-lg object-cover border border-amber-100 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-gray-900 text-sm truncate">{factory.name}</p>
                  <p className="text-gray-400 text-xs">{factory.city} · Est. {factory.established}</p>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <i className="ri-star-fill text-amber-400 text-xs"></i>
                  <span className="font-bold text-gray-800 text-sm">{factory.rating}</span>
                </div>
              </Link>

              {/* Pricing Cards */}
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => { setOrderType("sample"); setQty(1); setSubmitted(false); }}
                  className={`p-4 rounded-2xl border-2 text-left transition-all cursor-pointer ${orderType === "sample" ? "border-amber-500 bg-amber-50" : "border-gray-100 hover:border-amber-200"}`}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-7 h-7 flex items-center justify-center bg-amber-100 rounded-lg">
                      <i className="ri-test-tube-line text-amber-600 text-sm"></i>
                    </div>
                    <span className="font-bold text-gray-800 text-sm">Sample Order</span>
                  </div>
                  <div className="text-2xl font-black text-amber-600">${product.samplePrice}</div>
                  <div className="text-gray-400 text-xs mt-0.5">per unit · Min. 1 unit</div>
                  <div className="text-xs text-amber-600 font-medium mt-2">Perfect for testing quality</div>
                </button>
                <button
                  onClick={() => { setOrderType("bulk"); setQty(product.moq); setSubmitted(false); }}
                  className={`p-4 rounded-2xl border-2 text-left transition-all cursor-pointer ${orderType === "bulk" ? "border-emerald-500 bg-emerald-50" : "border-gray-100 hover:border-emerald-200"}`}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-7 h-7 flex items-center justify-center bg-emerald-100 rounded-lg">
                      <i className="ri-stack-line text-emerald-600 text-sm"></i>
                    </div>
                    <span className="font-bold text-gray-800 text-sm">Bulk Order</span>
                  </div>
                  <div className="text-2xl font-black text-emerald-600">${product.bulkPrice}</div>
                  <div className="text-gray-400 text-xs mt-0.5">per unit · Min. {product.moq} units</div>
                  <div className="text-xs text-emerald-600 font-medium mt-2">Best price for large orders</div>
                </button>
              </div>

              {/* Order Panel */}
              {!submitted && orderType && (
                <div className="border border-amber-100 rounded-2xl p-5 bg-white">
                  <p className="font-bold text-gray-800 text-sm mb-3">
                    {orderType === "sample" ? "Sample Order" : "Bulk Order"} — Set Quantity
                  </p>
                  <div className="flex items-center gap-3 mb-4">
                    <button
                      onClick={() => setQty((q) => Math.max(minQty, q - (orderType === "bulk" ? product.moq : 1)))}
                      className="w-10 h-10 flex items-center justify-center border border-amber-200 rounded-xl text-amber-600 hover:bg-amber-50 cursor-pointer font-bold text-lg"
                    >
                      <i className="ri-subtract-line"></i>
                    </button>
                    <input
                      type="number"
                      value={qty}
                      min={minQty}
                      onChange={(e) => setQty(Math.max(minQty, Number(e.target.value)))}
                      className="flex-1 border border-amber-200 rounded-xl px-3 py-2.5 text-base text-center font-bold focus:outline-none focus:border-amber-400"
                    />
                    <button
                      onClick={() => setQty((q) => q + (orderType === "bulk" ? product.moq : 1))}
                      className="w-10 h-10 flex items-center justify-center border border-amber-200 rounded-xl text-amber-600 hover:bg-amber-50 cursor-pointer font-bold text-lg"
                    >
                      <i className="ri-add-line"></i>
                    </button>
                  </div>
                  <div className="flex items-center justify-between bg-amber-50 rounded-xl px-4 py-3 mb-4">
                    <div>
                      <span className="text-xs text-gray-500">Estimated Total</span>
                      <div className="font-black text-amber-600 text-xl">${total}</div>
                    </div>
                    <div className="text-right">
                      <span className="text-xs text-gray-500">Unit Price</span>
                      <div className="font-bold text-gray-800">${unitPrice}/unit</div>
                    </div>
                  </div>
                  <button
                    onClick={() => setSubmitted(true)}
                    className={`w-full font-bold py-3.5 rounded-xl text-white text-sm transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 ${orderType === "sample" ? "bg-amber-500 hover:bg-amber-600" : "bg-emerald-600 hover:bg-emerald-700"}`}
                  >
                    <i className={orderType === "sample" ? "ri-test-tube-line" : "ri-stack-line"}></i>
                    {orderType === "sample" ? "Request Sample Order" : "Place Bulk Order"}
                  </button>
                </div>
              )}

              {submitted && (
                <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-5 text-center">
                  <div className="w-12 h-12 flex items-center justify-center bg-emerald-100 rounded-full mx-auto mb-3">
                    <i className="ri-checkbox-circle-fill text-2xl text-emerald-500"></i>
                  </div>
                  <p className="font-bold text-gray-900 mb-1">Order Request Sent!</p>
                  <p className="text-gray-500 text-xs mb-3">{qty} units of <strong>{product.name}</strong> from {factory.name}</p>
                  <button onClick={() => { setSubmitted(false); setOrderType(null); setQty(1); }} className="text-emerald-600 text-xs font-semibold hover:underline cursor-pointer">
                    Place another order
                  </button>
                </div>
              )}

              {!orderType && !submitted && (
                <p className="text-center text-gray-400 text-sm py-2">Select Sample or Bulk above to place your order</p>
              )}
            </div>
          </div>

          {/* Product Specs */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-16">
            <div className="bg-amber-50/50 border border-amber-100 rounded-2xl p-6">
              <h3 className="font-black text-gray-900 text-lg mb-4 flex items-center gap-2">
                <i className="ri-file-list-3-line text-amber-500"></i> Product Specifications
              </h3>
              <div className="space-y-3">
                {[
                  { label: "Category", value: product.category },
                  { label: "Sample Price", value: `$${product.samplePrice} / unit` },
                  { label: "Bulk Price", value: `$${product.bulkPrice} / unit` },
                  { label: "Min. Order Qty (MOQ)", value: `${product.moq} units` },
                  { label: "Factory", value: factory.name },
                  { label: "Origin", value: factory.city },
                  { label: "Established", value: factory.established.toString() },
                  { label: "Employees", value: factory.employees },
                ].map(({ label, value }) => (
                  <div key={label} className="flex items-center justify-between py-2 border-b border-amber-100 last:border-0">
                    <span className="text-gray-500 text-sm">{label}</span>
                    <span className="font-semibold text-gray-900 text-sm">{value}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white border border-amber-100 rounded-2xl p-6">
              <h3 className="font-black text-gray-900 text-lg mb-4 flex items-center gap-2">
                <i className="ri-building-2-line text-amber-500"></i> Factory Performance
              </h3>
              <div className="space-y-4">
                {[
                  { label: "Overall Rating", value: `${factory.rating}/5.0`, icon: "ri-star-fill", color: "text-amber-500", bar: (factory.rating / 5) * 100 },
                  { label: "Response Rate", value: factory.responseRate, icon: "ri-message-3-line", color: "text-amber-500", bar: parseInt(factory.responseRate) },
                  { label: "On-Time Delivery", value: factory.onTimeDelivery, icon: "ri-truck-line", color: "text-emerald-500", bar: parseInt(factory.onTimeDelivery) },
                ].map(({ label, value, icon, color, bar }) => (
                  <div key={label}>
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="flex items-center gap-1.5 text-sm text-gray-600">
                        <i className={`${icon} ${color} text-sm`}></i> {label}
                      </span>
                      <span className="font-bold text-gray-900 text-sm">{value}</span>
                    </div>
                    <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div className="h-full bg-amber-400 rounded-full transition-all" style={{ width: `${bar}%` }}></div>
                    </div>
                  </div>
                ))}
                <div className="pt-2">
                  <div className="flex items-center gap-2 text-sm text-gray-600 mb-1">
                    <i className="ri-price-tag-3-line text-amber-500"></i> Total Products
                    <span className="font-bold text-gray-900 ml-auto">{factory.productCount.toLocaleString()}+</span>
                  </div>
                  {factory.dropshipping && factory.warehouseCountries && (
                    <div className="mt-3 bg-emerald-50 border border-emerald-100 rounded-xl px-3 py-2.5">
                      <p className="text-xs font-bold text-emerald-700 mb-1 flex items-center gap-1">
                        <i className="ri-global-line"></i> Ships from:
                      </p>
                      <p className="text-emerald-600 text-xs">{factory.warehouseCountries.join(" · ")}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Related Products */}
          {relatedProducts.length > 0 && (
            <div>
              <h3 className="font-black text-gray-900 text-xl mb-6 flex items-center gap-2">
                <i className="ri-grid-line text-amber-500"></i>
                More from {factory.name}
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-5">
                {relatedProducts.map((rp) => (
                  <Link
                    key={rp.id}
                    to={`/factory-product/${factory.id}/${rp.id}`}
                    className="bg-white border border-amber-100 rounded-2xl overflow-hidden hover:border-amber-300 transition-all cursor-pointer group"
                  >
                    <div className="w-full h-44 overflow-hidden">
                      <img src={rp.image} alt={rp.name} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300" />
                    </div>
                    <div className="p-4">
                      <span className="bg-amber-100 text-amber-700 text-xs px-2 py-0.5 rounded-full">{rp.category}</span>
                      <p className="font-bold text-gray-900 text-sm mt-2 mb-2 leading-tight">{rp.name}</p>
                      <div className="flex items-center justify-between text-xs text-gray-500">
                        <span>Sample: <strong className="text-amber-600">${rp.samplePrice}</strong></span>
                        <span>Bulk: <strong className="text-emerald-600">${rp.bulkPrice}</strong></span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default FactoryProductDetailPage;
